import {format, formatDistanceToNow} from 'date-fns'

import {MemexColumnDataType} from './api/columns/contracts/memex-column'
import {ItemType} from './api/memex-items/item-type'
import type {RoadmapZoomLevel} from './api/view/contracts'
import {ToastType} from './components/toasts/types'
import {joinOxford} from './helpers/join-oxford'
import type {META_QUALIFIERS} from './helpers/meta-qualifiers'
import type {ConfirmationSource} from './helpers/workflow-utilities'
import {formatDateUtc} from './pages/roadmap/date-utils'

const singularOrPlural = (count: number, singular: string, plural: string) => (count === 1 ? singular : plural)
export const Resources = {
  accessProhibited: 'Access prohibited',
  addColumnNameValue: (columnName: string) => `Add ${columnName.toLowerCase()}…`,
  addItem: 'Add item',
  updateTitleLabel: 'Update project title',
  archiveGroupItemsConfirmationMessage: (itemsCount: number) =>
    `Are you sure you want to archive ${singularOrPlural(itemsCount, 'this item', `${itemsCount} items`)}?`,
  deleteGroupItemsConfirmationMessage: (itemsCount: number) =>
    `Are you sure you want to delete ${singularOrPlural(
      itemsCount,
      'this item',
      `${itemsCount} items`,
    )} from the project?`,
  archiveGroupItemsConfirmationTitle: `Archive all items?`,
  deleteGroupItemsConfirmationTitle: `Delete all items?`,
  cannotAddItemsWhenGroupByMilestone: 'Cannot add items when grouped by milestone',
  cannotAddItemsWhenGroupByRepository: 'Cannot add items when grouped by repository',
  cannotAddItemsWhenGroupByIssueType: 'Cannot add items when grouped by type',
  cannotAddTitle: (field: string) => `Cannot add ${field}`,
  cannotAddPullRequestsDescription: (field: string) => `Pull requests cannot have ${field}`,
  cannotEditTitle: 'Update permissions',
  cannotEditItemContent:
    "You can't edit this item because it belongs to a repository that you don't have write-access to.",
  cannotAddAssigneesToDraftContent: 'Convert this draft to an issue to add an assignee',
  confirmDialog: 'Got it!',
  iterationLabel: {
    current: 'Current',
    planned: 'Planned',
    completed: 'Completed',
    break: 'Break',
  },
  cannotReorderForSortMessage: 'Cannot reorder items between sorted values',
  cannotReorderForSortAction: 'Remove sorting',
  changesSaved: 'Changes saved',
  columnLimitExceeded: 'Column limit is too large, must be less than 1,000,000.',
  createNewItemOrAddExistingIssueAriaLabel: 'Create new item or add existing item',
  dateEditor: 'Date editor',
  defaultArchiveConfirmationMessage: (pluralize: boolean, itemCount: number) =>
    `Are you sure you want to archive ${pluralize ? `these ${itemCount} items` : 'this item'}?`,
  defaultArchiveConfirmationTitle: (pluralize: boolean) => `Archive item${pluralize ? 's' : ''}?`,
  deleteFieldDialogTitle: 'Delete field?',
  deleteField: (field: string) => `This will delete the field “${field}”`,
  displayButtonOnboardingTitle: 'Display options have a new home!',
  displayButtonOnboardingDescription: 'You can now find layouts, fields, groups, and more in this menu.',
  draftConvertPromptDescription: 'Convert to issue to edit this cell',
  draftConvertPromptRegroupDescription: (field: string) => `Convert this draft to an issue to add ${field}`,
  draftConvertPromptTitle: 'Convert to issue',
  duplicateItemForGroup: 'Content already exists in this group',
  duplicateView: ({isDirty}: {isDirty: boolean}) => (isDirty ? 'Save changes to new view' : 'Duplicate view'),
  saveToCurrentView: 'Save to current view',
  edit: 'Edit',
  editTitle: 'Edit title',
  emptyColumnNameValue: {
    number: 'Enter a number…',
    text: 'Enter text…',
    date: 'Enter a date…',
    iteration: 'Choose an iteration…',
    singleSelect: 'Choose an option…',
    linkedPullRequest: 'No linked pull requests',
  },
  excludeSuggestion: 'Exclude',
  fieldMustBeAnInteger: 'This field must be an integer',
  fieldMustBePositiveOrZero: 'This field must be positive or 0.',
  filterByKeyboardOrByField: 'Filter by keyword or by field',
  viewFilters: 'View filters',
  genericErrorMessage: 'Sorry, something went wrong',
  rateLimitedErrorMessage: 'Rate limit exceeded. Please try again later.',
  genericFormErrorMessage: 'This form has errors',
  groupHeaderMenu: (groupName: string, isColumn: boolean) =>
    `Actions for ${isColumn ? 'column' : 'group'}: ${groupName}`,
  invalidFieldValues: ({name, value, fields}: {name: string; value: string; fields: string}) =>
    `Invalid \`${name}\`: ${value}. ${fields} are acceptable values for \`${name}\``,
  issue: 'issue',
  sidePanelRegionNameLabel: 'Side panel',
  sidePanelProjectInfoLabel: 'Project information',
  sidePanelBulkAddLabel: 'Add items to project',
  sidePanelRecapLabel: 'Recap',
  sidePanelItemNotSupported: 'Pull requests are not currently supported in the side panel.',
  sidePanelItemNotFound: "The item is not in the project, or you don't have access to view it.",
  sidePanelCloseConfirmation: {
    title: 'Discard changes?',
    content: 'You have unsaved changes. Are you sure you want to discard them?',
    cancelButtonContent: 'Keep editing',
    confirmButtonContent: 'Close and discard',
  },
  sidePanelPinLabel: 'Pin side panel',
  sidePanelUnpinLabel: 'Unpin side panel',
  sidePanelToolbarLabel: 'Side panel controls',
  sidePanelCopyPermalink: 'Copy project item permalink',
  issueButtonLabel: {
    closeIssue: 'Close issue',
    reopenIssue: 'Reopen',
    closeWithComment: 'Close with comment',
    reopenWithComment: 'Reopen and comment',
    reopenIssueLong: 'Reopen issue',
    closeAsCompleted: 'Close as completed',
    closeAsCompletedDescription: 'Done, closed, fixed, resolved',
    closeAsNotPlanned: 'Close as not planned',
    closeAsNotPlannedDescription: 'Won’t fix, can’t repro, duplicate, stale',
  },
  issueSideButtonMoreOptionsLabel: 'More options',
  iterationCustomModelError: 'Iteration editor requires a custom model',
  iterationDurationQtyLow: (minAllowedValue: number) => `Iteration length must be ${minAllowedValue} or more`,
  iterationDurationQtyHigh: (maxAllowedValue: number) => `Iteration length must be ${maxAllowedValue} or less`,
  iterationDurationQtyCannotBeBlank: 'Iteration length cannot be blank',
  iterationDurationLabel: 'Duration',
  iterationNotFound: 'Iteration not found',
  iterationStartDateLabel: 'Starts on',
  jumpToBottom: 'Jump to bottom',
  newField: 'New field',
  newItemAddedToBottomOfColumn: 'The new item has been added to the bottom of the column.',
  newItemAddedToBottomOfRoadmap: 'The new item has been added to the bottom of the roadmap.',
  newItemAddedToBottomOfTable: 'The new item has been added to the bottom of the table.',
  newItemFilterWarning: "The new item is hidden by this view's filters.",
  viewItem: 'Open item',
  newItemFindIssuePlaceholder: (canCreateIssue: boolean) =>
    canCreateIssue
      ? 'Search issues and pull requests, create a new issue, or add multiple items'
      : 'Search issues and pull requests, or add multiple items',
  newItemPlaceholder: 'Start typing to create an item, or type # to select a repository',
  newItemPlaceholderAriaLabel: 'Start typing to create an item, or type hashtag to select a repository',
  newIterationField: (number: string) => `Iteration${number}`,
  newOptionsCount: 'New options count',
  noPermissionEmptyColumnValue: (columnName: string) => `No ${columnName}`,
  noDescriptionProvided: 'No description provided',
  noneYet: 'None yet',
  hasSuggestion: 'Has',
  noSuggestion: 'No',
  numberEditor: 'Number editor',
  projectNameRequired: 'Project name is required.',
  projectSettingsSaved: 'Project settings saved successfully',
  pullRequest: 'pull request',
  removeFieldDialogTitle: 'Remove field from project?',
  removeIssueField: (field: string) => `This will remove the field “${field}”`,
  repoArchivedErrorMessage: "You can't edit this field because this repository is archived",
  repoIsEmpty: 'This repository is either empty or all items were already added to this project.',
  repoListInstructions: 'Pick a repository to add issues and pull requests from',
  repoListTitle: 'Select a repository',
  repoPickerFilterPlaceholder: 'Search repositories',
  repoPickerNoneFoundTitle: 'No repositories found',
  repoPickerNoneFoundBody: 'No repositories found matching your search query.',
  requiredFieldErrorMessage: 'Field name is required.',
  requiresCustomColumn: 'requires a custom column',
  reviewersCount: (count: number) => `${count} reviewer${count > 1 ? 's' : ''}`,
  progressCount: ({percent, total}: {percent: number; total: number}) => `${percent}% of ${total}`,
  progressScalarCount: ({completed, total}: {completed: number; total: number}) => `${completed} of ${total} completed`,
  progressPercentCount: (percentage: number) => `${percentage}% completed`,
  noItemsInSearchResult: "We couldn't find any items matching that search query.",
  singleSelectCustomModelError: 'Single select editor requires a custom model',
  singleSelectOptionLimitWarning: (count: number) => `Maximum of ${count} options reached`,
  clearFilter: 'Clear filter',
  discardChanges: 'Discard',
  saveChanges: 'Save',
  cancelChanges: 'Cancel',
  setDefaultChanges: 'Save view',
  saveToNewChart: 'Save to new chart',
  saveFilter: 'Save filter',
  selectRepo: 'Select repository',
  set: 'Set',
  reset: 'Reset',
  confirmSetDefaultChangesTitle: (viewName?: string | null) =>
    viewName && viewName.length > 0 ? `Save display options for ${viewName}?` : 'Set as the default for everyone?',
  confirmSetDefaultChangesTitleFilter: (viewName?: string | null) =>
    viewName && viewName.length > 0 ? `Save filters for ${viewName}?` : 'Set as the default for everyone?',
  confirmSetDefaultChangesTitleCombined: (viewName?: string | null) =>
    viewName && viewName.length > 0
      ? `Save filters and display options for ${viewName}?`
      : 'Set as the default for everyone?',
  confirmSetDefaultChangesDescription:
    'Publishing this configuration will make it the default for everyone viewing the project.',
  confirmSetDefaultChangesDescriptionFilter: 'Saving these filters will make it the default for everyone in this view.',
  confirmSetDefaultChangesDescriptionDisplay:
    'Saving these display options will make it the default for everyone in this view.',
  confirmSetDefaultChangesDescriptionCombined:
    'Saving these options will make it the default for everyone in this view.',
  /**
   * Returns the appropriate confirmation description based on dirty state.
   * @param filterDirty - Whether the filter has unsaved changes
   * @param configDirty - Whether the config/display options have unsaved changes
   * @param includeDisplayFallback - Whether to fall back to display description when only config is dirty (default: false)
   */
  getConfirmationDescription: ({
    filterDirty,
    configDirty,
    includeDisplayFallback = false,
  }: {
    filterDirty: boolean
    configDirty: boolean
    includeDisplayFallback?: boolean
  }): string => {
    if (filterDirty && configDirty) {
      return Resources.confirmSetDefaultChangesDescriptionCombined
    }
    if (filterDirty) {
      return Resources.confirmSetDefaultChangesDescriptionFilter
    }
    if (includeDisplayFallback) {
      return Resources.confirmSetDefaultChangesDescriptionDisplay
    }
    return Resources.confirmSetDefaultChangesDescriptionFilter
  },
  /**
   * Returns the appropriate confirmation title based on dirty state.
   * @param filterDirty - Whether the filter has unsaved changes
   * @param configDirty - Whether the config/display options have unsaved changes
   * @param viewName - Optional view name to include in the title
   */
  getConfirmationTitle: ({
    filterDirty,
    configDirty,
    viewName,
  }: {
    filterDirty: boolean
    configDirty: boolean
    viewName?: string | null
  }): string => {
    if (filterDirty && configDirty) {
      return Resources.confirmSetDefaultChangesTitleCombined(viewName)
    }
    if (filterDirty) {
      return Resources.confirmSetDefaultChangesTitleFilter(viewName)
    }
    return Resources.confirmSetDefaultChangesTitle(viewName)
  },
  selectionMaximumExceeded: (maximum: number) =>
    `More than ${maximum} cells are selected. Deselect some cells to perform bulk edits.`,
  sortBy: 'Sort by',
  statusFieldEmptyError: 'Error: At least one valid option is required for the status field',
  sortDatesAscending: 'Ascending order: oldest to newest',
  sortDatesDescending: 'Descending order: newest to oldest',
  sortNumbersAscending: 'Ascending order: lowest to highest',
  sortNumbersDescending: 'Descending order: highest to lowest',
  sortSingleSelectAscending: 'Ascending order: first to last',
  sortSingleSelectDescending: 'Descending order: last to first',
  sortStringsAscending: 'Ascending order: A to Z',
  sortStringsDescending: 'Descending order: Z to A',
  tableHeaderContextMenu: {
    selectColumn: 'Select column',
    sortAscending: 'Sort ascending',
    sortAscendingActive: 'Sorted ascending',
    sortDescending: 'Sort descending',
    sortDescendingActive: 'Sorted descending',
    filterValues: 'Filter by values…',
    filterByType: 'Filter by type or state…',
    groupByValues: 'Group by values',
    groupByActive: 'Grouped by field',
    sliceByValues: 'Slice by values',
    hideField: 'Hide field',
    fieldSettings: 'Field settings…',
  },
  textEditor: 'Text editor',
  titleCannotBeBlank: 'Title cannot be blank',
  titleContainsReservedColonCharacter: "Field name can't contain colons",
  titleHasAlreadyBeenTaken: 'This field name has already been taken',
  titleIsReserved: 'This field name is a reserved word',
  unableToAssignUsersToConvertedIssue: (logins: Array<string>) =>
    `Unable to assign the following users to the converted issue: ${logins.join(', ')}`,
  unableToPasteLabelsBetweensRepos: 'Labels can only be copied within the same repository',
  unableToPasteAssigneesBetweensRepos: 'Assignees can only be copied within the same repository',
  unableToPasteMilestonesBetweensRepos: 'Milestones can only be copied within the same repository',
  unableToPasteIssueTypesBetweensRepos: 'Issue types can only be copied within the same repository',
  noValidUpdates: 'No valid updates to apply',
  issueType: 'Issue type',
  unableToSetColumnOnPullRequest: (field: string) => {
    const makePlural = !field.endsWith('s')
    return `${field}${makePlural ? 's' : ''} cannot be applied to pull requests`
  },
  unableToSetColumnOnDraftIssue: (field: string) => {
    const makePlural = !field.endsWith('s')
    return `${field}${makePlural ? 's' : ''} cannot be applied to draft issues`
  },
  unableToSetIssueFieldsAcrossOwners: (owner: string) =>
    `Issue fields can only be applied to issues within the ${owner} organization`,
  unableToPasteMismatchedDataTypes: 'Cannot paste data of different types',
  unableToPasteMissingSingleSelectOption: 'Cannot paste: value does not match any allowed option',
  updateColumnOptionsFailed: 'Column options failed to update.',
  forbiddenErrorMessage: "You don't have permission to edit this field.",
  undefined: 'undefined' as const,
  unsortBy: 'Unsort by',
  untitledProject: 'Untitled project',
  untitledUserProject: (user: string) => `@${user}'s untitled project`,
  viewNameRequired: 'View name cannot be empty',
  removeTitle: 'Remove',
  resultCount: (num: number) => `${num} result${num === 1 ? '' : 's'}.`,
  itemType: (itemType: ItemType) => {
    switch (itemType) {
      case ItemType.Issue:
        return 'Issue'
      case ItemType.PullRequest:
        return 'Pull request'
      case ItemType.DraftIssue:
        return 'Draft issue'
      case ItemType.RedactedItem:
        return 'Unknown item'
    }
  },
  serviceUnavailable: 'We are having trouble connecting to your project. Please try again or reload the page.',
}

export const HistoryResources = {
  updateItem: 'update item',
  updateItems: 'update items',
  delete: 'delete',
  paste: 'paste',
  fillCells: 'fill cells',
  archive: (count: number) => singularOrPlural(count, 'archive item', 'archive items'),
  moveItem: 'move item',
}

export const RouteTitleResources = {
  item: (title: string, itemTitle: string) => `${itemTitle} · ${title}`,
  view: (title: string, name?: string) => (name ? `${name} · ${title}` : title),
  archive: (title: string) => `Archive · ${title}`,
  settings: (title: string, name?: string) => (name ? `${name} · Settings · ${title}` : `Settings · ${title}`),
  workflows: (title: string) => `Workflows · ${title}`,
  insights: (title: string) => `Insights · ${title}`,
  newIssue: (otherViewInfo: string) => `New issue · ${otherViewInfo}`,
}

export const TopBarResources = {
  insightsButton: 'Insights',
  projectDetailsButton: 'Project details',
  viewMoreOptions: 'View more options',
  viewRecapButton: 'Recap',
}

export const MilestoneResources = {
  closed: 'Closed',
  noDueDate: 'No due date',
  addedUnsupportedItemToast: 'Drafts can’t be added with a milestone. Item was added to No milestone group',
  pastDueBy: (milestoneDueDate: Date) => `Past due by ${formatDistanceToNow(milestoneDueDate)}`,
  dueOn: (milestoneDueDate: Date) => `Due by ${format(milestoneDueDate, 'MMMM d, yyyy')}`,
}

export const FilterQueryResources = {
  invalidFilterQuotes: `Invalid filter: Unmatched quotation mark`,
  updatedValueErrorMessage:
    'Supported format is <@today-X, where X is a number and unit. Unit can be one of d,w or m ex: 7d',
  lastUpdatedValueErrorMessage: 'Supported format is XXdays (except 0days)',
  unknownFieldFilter: (field: string) => `Invalid filter: Unknown field name "${field}"`,
  unknownFieldFilters: (fields: Array<string>) =>
    `Invalid filter: Unknown field name${fields.length > 1 ? 's' : ''} ${fields.map(f => `"${f}"`).join(', ')}`,
  noIterationMatch: (iterationValue: string, fieldName: string) =>
    `Invalid value: "${iterationValue}" doesn't match any iterations in the "${fieldName}" field`,
  noOptionsMatch: (optionsValue: string, fieldName: string) =>
    `Invalid value: "${optionsValue}" doesn't match options for the "${fieldName}" field`,
  invalidNumberValue: (value: string) => `Invalid value: "${value}" is not a number`,
  invalidDateValue: (value: string) => `Invalid value: "${value}" is not a valid date (must be formatted YYYY-MM-DD)'`,
  invalidPwlDateValue: (provider: string, value: string) =>
    `Invalid value <pre>${value}</pre> for <pre>${provider}</pre>. Fix by using date format YYYY-MM-DD`,
  invalidQualifier: (value: Array<string>, expectedValues: ObjectValues<typeof META_QUALIFIERS>) =>
    `Invalid value: "${value.join(', ')}" does not match the supported values "${joinOxford(
      Object.values(expectedValues),
      'or',
    )}"`,
}

const valueNoun = (fieldType: MemexColumnDataType): [string, string] =>
  fieldType === MemexColumnDataType.SingleSelect
    ? ['option', 'options']
    : fieldType === MemexColumnDataType.Iteration
      ? ['iteration', 'iterations']
      : ['value', 'values']

export const SettingsResources = {
  deleteProjectWarning: 'Unexpected bad things will happen if you don’t read this!',
  deleteProjectTitle: 'Are you absolutely sure?',
  deleteProjectConfirmation: 'I understand the consequences, delete this project',
  deleteProjectInputLabel: 'Type in the name of the project to confirm that you want to delete this project.',
  noIterationsTitle: "You're out of iterations.",
  noIterationsDescription: 'Create an iteration using the "+ Add iteration" button above.',
  noCompletedIterationsTitle: 'No completed iterations.',
  noCompletedIterationsDescription: 'Iterations will automatically move here over time.',
  organizationOwnedProjectVisibilityRestriction: 'Only organization owners can change project visibility.',
  projectIsPublic: 'This project is currently public.',
  projectIsPrivate: 'This project is currently private.',
  projectIsInternal: 'This project is currently internal to your enterprise.',
  unselectCollaborator: (name: string) => `Unselect ${name}`,
  selectCollaborator: (name: string) => `Select ${name}`,
  userOwnedProjectVisibilityRestriction: 'Only admins can change project visibility.',
  willPushActiveIterations: (n: number) =>
    `${n} active ${n === 1 ? 'iteration' : 'iterations'} will also be pushed forward.`,
  /** Examples: Delete option?, Delete 12 options?, Delete iteration? */
  deleteValuesTitle: (fieldType: MemexColumnDataType, valuesCount: number) =>
    `Delete ${singularOrPlural(valuesCount, valueNoun(fieldType)[0], `${valuesCount} ${valueNoun(fieldType)[1]}`)}?`,
  /** Examples: Delete option, Delete 12 options, Delete iteration */
  deleteValues: (fieldType: MemexColumnDataType, valuesCount: number) =>
    `Delete ${singularOrPlural(valuesCount, valueNoun(fieldType)[0], `${valuesCount} ${valueNoun(fieldType)[1]}`)}`,
  /** Example: This will permanently delete these iterations from the "Iteration" field. */
  deleteValuesIntro: (fieldType: MemexColumnDataType, fieldName: string, valuesCount: number) =>
    `This will permanently delete ${singularOrPlural(
      valuesCount,
      `this ${valueNoun(fieldType)[0]}`,
      `these ${valueNoun(fieldType)[1]}`,
    )} from the "${fieldName}" field and will impact everyone using this project.`,
  /** Example: The option will be cleared from 12 items in this project. */
  deleteValuesAffectedItemsWarning: (fieldType: MemexColumnDataType, valuesCount: number, affectedItemsCount: number) =>
    `The ${singularOrPlural(
      valuesCount,
      ...valueNoun(fieldType),
    )} will be permanently deleted from ${affectedItemsCount} ${singularOrPlural(
      affectedItemsCount,
      'item',
      'items',
    )} in this project.`,
  deleteValuesUnknownAffectedItemsWarning: (fieldType: MemexColumnDataType, valuesCount: number) =>
    `The ${singularOrPlural(
      valuesCount,
      ...valueNoun(fieldType),
    )} will be permanently deleted from any items in this project.`,
  deleteIterationValuesIntro: (fieldName: string, valuesCount: number) =>
    `This will permanently delete ${singularOrPlural(
      valuesCount,
      `this iteration value`,
      `these iteration values`,
    )} from the "${fieldName}" field and will impact everyone using this project.`,
  deleteIterationValuesAffectedItemsWarning: (valuesCount: number, affectedItemsCount: number) =>
    `${singularOrPlural(
      valuesCount,
      `This iteration value`,
      `These iteration values`,
    )} will be removed from ${affectedItemsCount} ${singularOrPlural(
      affectedItemsCount,
      'item',
      'items',
    )} in this project.`,
  deleteIterationValuesUnknownAffectedItemsWarning: (valuesCount: number) =>
    `${singularOrPlural(
      valuesCount,
      `This iteration value`,
      `These iteration values`,
    )} will be removed from any items in this project.`,
  cannotBeUndone: 'This cannot be undone.',
}

export const ItemUpdateValidationResources = {
  Repository: {
    Alerts: {
      NeedsConversion: {
        title: Resources.draftConvertPromptTitle,
        content: 'Convert this draft to an issue to add it to this repository',
        confirmButtonContent: Resources.confirmDialog,
      },
      CannotTransferPulls: {
        title: 'Unable to transfer pull request',
        content: 'Pull requests cannot be moved between repositories.',
        confirmButtonContent: Resources.confirmDialog,
      },
      IssueNeedsTransfer: {
        title: 'Transfer issue',
        confirmButtonContent: Resources.confirmDialog,
      },
    },
  },
  Milestone: {
    Alerts: {
      NeedsConversion: {
        title: Resources.draftConvertPromptTitle,
        content: 'Drafts can’t be added to milestones. Convert this draft to an issue to add it to a milestone.',
        confirmButtonContent: Resources.confirmDialog,
      },
      NeedsTransfer: {
        title: 'New milestone required',
        confirmButtonContent: Resources.confirmDialog,
      },
    },
  },
  IssueType: {
    Alerts: {
      NeedsConversion: {
        title: Resources.cannotAddTitle('type'),
        content: Resources.draftConvertPromptRegroupDescription('an issue type'),
        confirmButtonContent: Resources.confirmDialog,
      },
      CannotAssignPulls: {
        title: Resources.cannotAddTitle('type'),
        content: Resources.cannotAddPullRequestsDescription('an issue type'),
        confirmButtonContent: Resources.confirmDialog,
      },
      NoTypesForRepo: {
        title: Resources.cannotAddTitle('type'),
        content: 'Issue types are excluded from this repository.',
        confirmButtonContent: Resources.confirmDialog,
      },
      NeedsTransfer: {
        title: Resources.cannotAddTitle('type'),
        confirmButtonContent: Resources.confirmDialog,
      },
    },
  },
  IssueField: {
    Alerts: {
      NeedsConversion: {
        title: Resources.cannotAddTitle('issue field'),
        content: Resources.draftConvertPromptRegroupDescription('an issue field'),
        confirmButtonContent: Resources.confirmDialog,
      },
      CannotAssignPulls: {
        title: Resources.cannotAddTitle('issue field'),
        content: Resources.cannotAddPullRequestsDescription('issue fields'),
        confirmButtonContent: Resources.confirmDialog,
      },
      OwnerMismatch: (owner: string) => ({
        title: Resources.cannotAddTitle('issue field'),
        content: Resources.unableToSetIssueFieldsAcrossOwners(owner),
        confirmButtonContent: Resources.confirmDialog,
      }),
      GenericFailure: {
        title: Resources.cannotAddTitle('issue field'),
        content: Resources.genericErrorMessage,
        confirmButtonContent: Resources.confirmDialog,
      },
    },
  },
}

export const WorkflowResources = {
  editWorkflow: 'Edit',
  saveWorkflow: 'Save and turn on workflow',
  saveWorkflowWithoutEnable: 'Save workflow',
  discardWorkflow: 'Discard',
  deleteWorkflow: 'Delete',
  readOnlyLabel: 'Viewing mode. Click edit to make changes to this workflow.',
  duplicateWorkflowName: 'A workflow with this name already exists',
  emptyFilterDefault: 'Filter cannot be empty',
  emptyFilterAutoAdd: 'An empty filter will match all newly created and updated Issues and Pull Requests',
  emptyWorkflowName: 'Workflow name cannot be empty',
  invalidActionField: 'A valid field is required',
  invalidActionFieldValue: 'A valid value is required',
  invalidNoAction: 'An action is required',
  invalidNoContentType: 'A type is required',
  invalidNoQuery: 'A query is required',
  invalidNoQueryOrFieldId: 'A query or a field id is required',
  invalidNoRepository: 'A repository is required',
  invalidNoValue: 'A value is required',
  workflowNameTooLong: 'Workflow name cannot be longer than 250 characters',
  invalidQuery: 'Filter contains invalid syntax',
  isClosed: 'is:closed',
  isMerged: 'is:merged',
  isNewlyAdded: 'is added',
  isReopened: 'is:reopened',
  addItemBlockLabel: 'Add the item to the project',
  addSubIssuesBlockLabel: 'Add sub-issues to the project',
  closeItemBlockLabel: 'Close the issue',
  getItemsBlockLabel: 'When the filter matches a new or updated item',
  getSubIssuesBlockLabel: 'When an item in the project has sub-issues',
  itemAddedToProjectLabel: 'When an item is added to the project',
  itemReopenedLabel: 'When an item is reopened',
  itemClosedLabel: 'When an item is closed',
  codeChangesRequestedLabel: 'When a pull request has a review requesting changes',
  codeReviewApprovedLabel: 'When a pull request is approved',
  pullRequestMergedLabel: 'When a pull request is merged',
  pullRequestConnectedLabel: 'When a pull request is linked to an issue',
  autoArchiveItemsLabel: 'Filter items',
  projectItemColumnUpdateLabel: 'When the status is updated',
  setValueLabel: 'Set value',
  selectContentType: 'Select a content type',
  archiveItemLabel: 'Archive item',
  autoAddManualTip:
    'Going forward, all new or updated items that match your filter will be auto added. You can also manually add items that currently match your filter',
  manualRunConfirmationButton: (confirmationSource: ConfirmationSource) =>
    confirmationSource === 'update'
      ? 'Update workflow'
      : confirmationSource === 'disable'
        ? 'Disable workflow?'
        : 'Save and enable',
  manualRunConfirmationMessage: (confirmationSource: ConfirmationSource, itemCount?: number) =>
    confirmationSource === 'disable'
      ? 'You have to disable this workflow to be able to edit this field.'
      : `${
          confirmationSource === 'update' ? 'Updating' : 'Enabling'
        } this workflow will archive <strong>${itemCount}</strong> item${
          itemCount && itemCount > 1 ? 's' : ''
        } that currently meet the filter conditions.
      <br><br>As future items match filter conditions, they will be automatically archived.`,
  manualRunConfirmationTitle: (confirmationSource: ConfirmationSource) =>
    confirmationSource === 'update'
      ? 'Update workflow?'
      : confirmationSource === 'disable'
        ? 'Disable workflow?'
        : 'Enable workflow?',
  reviewApproved: 'review:approved',
  /** Example: This option is used in 3 workflows. Deleted it will disable the workflows and invalidate any actions referencing them. */
  deleteValuesAffectedWorkflowsWarning: (
    fieldType: MemexColumnDataType,
    valuesCount: number,
    affectedWorkflowsCount: number,
  ) =>
    `${singularOrPlural(
      valuesCount,
      `This ${valueNoun(fieldType)[0]} is`,
      `These ${valueNoun(fieldType)[1]} are`,
    )} used in ${affectedWorkflowsCount} ${
      affectedWorkflowsCount === 1 ? 'workflow' : 'workflows'
    }. Deleting ${singularOrPlural(valuesCount, 'it', 'them')} will disable the ${singularOrPlural(
      affectedWorkflowsCount,
      'workflow',
      'workflows',
    )} and invalidate any actions referencing ${singularOrPlural(valuesCount, 'it', 'them')}.`,
  reviewChangesRequested: 'review:changes_requested',
  deleteWorkflowError: 'Could not delete workflow, please try again',
  deleteWorkflowSuccess: 'Workflow deleted',
  deleteWorkflowCallToAction: 'Delete workflow',
  deleteWorkflowConfirmationTitle: 'Delete workflow',
  deleteWorkflowConfirmationContent: 'Are you sure you want to delete this workflow? This action cannot be undone.',
  deleteWorkflowConfirmationButton: 'Delete',
}

export const CreateWorkflowResources = {
  defaultWorkflowName: 'New auto-add workflow',
  duplicateWorkflowLabel: 'Duplicate workflow',
  newWorkflowLabel: 'New workflow',
  workflowNameInputLabel: 'Workflow name',
  duplicateButton: 'Duplicate',
  autoAddWorkflowLimitReached:
    'Maximum number of auto-add workflows reached. Delete a workflow or upgrade your plan to continue.',
  newWorkflowName: (workflowTemplateName: string) => `Duplicate of ${workflowTemplateName}`,
}

export const ManageAccessResources = {
  privacySettingsAdmin: 'Everyone in the organization is an admin of this project.',

  privacySettingsPublicLabel: 'Public project',
  privacySettingsPrivateLabel: 'Private project',
  privacySettingsInternalLabel: 'Internal project',

  privacySettingsPublicDescription: 'This project is public and visible to anyone.',
  privacySettingsPrivateDescription: 'Only those with access to this project can view it.',
  privacySettingsInternalDescription: 'Everyone in the organization can view this project.',

  collaboratorDropDownAdmin: 'Can see, make changes to, and add new collaborators to this project.',
  collaboratorDropDownWrite: 'Can see and make changes to this project.',
  collaboratorDropDownRead: 'Can see this project.',
  collaboratorDropDownNone:
    'Organization members will only be able to see this project if it’s public. To give an organization member additional access, they can be added as part of a team or as a collaborator.',
  addCollaboratorPlaceholderUserProject: 'Search by username',
  addCollaboratorPlaceholderOrgProject: 'Search by username or team',

  collaboratorFilterPlaceholderUserProject: 'Find a collaborator',
  collaboratorFilterPlaceholderOrgProject: 'Find a collaborator or a team',
}

export const DescriptionEditorResources = {
  saveButton: 'Save',
  cancelButton: 'Cancel',
  shortDescriptionTitle: 'Short description',
  shortDescriptionPlaceholder: 'A short description about this project.',
  readmeTitle: 'README',
  readmePlaceholder: 'Let everyone know what this project is about, how to use it and link to important resources.',
  errorMessage: 'An error occurred while saving.',
}

export const DragAndDropResources = {
  instructions: (title: string | number) => `Moving ${title}`,
  firstItemInList: 'First item in list.',
  lastItemInList: 'Last item in list.',
  moveUp: 'Move item up in one increment',
  moveDown: 'Move item down in one increment',
  cancelDrag: 'Cancel drag mode',
  endDrag: 'Place item',
  movedBetween: (itemA: string, itemB: string) => `Moved between ${itemA} and ${itemB}.`,
  successfulMove: (title: string, itemA: string, itemB: string) =>
    `Successfully moved ${title} between ${itemA} and ${itemB}.`,
  successfulMoveTop: (title: string) => `Successfully moved ${title} to the first position in list`,
  successfulMoveBottom: (title: string) => `Successfully moved ${title} to the last position in list`,
  cancelMove: (title: string) => `${title} not moved.`,
  defaultMoveDialogValidationMessage: 'Please enter a valid position',
}

export const InsightsResources = {
  addInsightsChartNamePlaceholder: 'Add a name for this chart',
  defaultBurnupChartName: 'Burn up',
  defaultBurnupChartDescription:
    'The Burn up chart shows the progress of your project items over time, showing how much work has been completed and how much is left to do. Use this chart to view progress, spot trends, and identify bottlenecks to help move the project forward.',
  defaultStatusChartName: 'Status chart',
  defaultStatusChartDescription: 'This chart shows the current status for the total number of items in your project.',
  invalidFieldLabel: 'Missing field',
  invalidFieldErrorMessage: 'The selected field no longer exists',
  operationLabel: {
    count: 'Count of',
    sum: 'Sum of',
    avg: 'Average of',
    min: 'Minimum of',
    max: 'Maximum of',
  },
  sideNavNewChartButton: 'New chart',
  sideNavDefaultCharts: 'Default charts',
  sideNavCustomCharts: 'Custom charts',
  sideNavPreviewFeatureText: 'Historical charts are currently in limited feature preview.',
  upsellDialogTitle: 'Get unlimited charts',
  upsellDialogSubtitle: 'Upgrade your plan to get:',
  upsellDialogBullet1: 'Unlimited custom charts for private projects',
  upsellDialogBullet2: 'Historical insights (Team or Enterprise plans only)',
  upsellDialogBullet3: 'All the additional perks of a premium GitHub plan',
  upsellDialogCancelButton: 'Cancel',
  items: 'Items',
  upsellDialogUpgradePlanButton: 'Upgrade plan',
  planUpgradeText: (chartLimit: number) =>
    `Private projects are limited to ${chartLimit} custom charts.  Make your project public or upgrade your plan to add more charts.`,
  periodNavigationLinkAnnouncement: (period: string) => `Displaying results for ${period}`,
  negateStr: (value: string) => `No ${value}`,
}

export const RoadmapResources = {
  addItemToToday: 'Add item to today',
  dragSashAriaLabel: 'Drag to resize the table column',
  noDateFieldsConfiguredTooltip: 'Create a date or iteration field to specify dates',
  noIterationConfiguredTooltip: (dateString: string) =>
    `Create an iteration containing ${dateString} to add items here`,
  iterationSuffix: (variant: 'start' | 'end') => (variant === 'start' ? 'start' : 'end'),
  startDate: 'Start date',
  endDate: 'Target date',
  noStartDate: 'No start date',
  noEndDate: 'No target date',
  noDateSet: 'No date set',
  gettingStarted: {
    popoverTitle: 'Welcome to Roadmap!',
    popoverDismissButton: 'Got it!',
    projectNeedsField: 'Your project needs at least one date or iteration field to get started.',
    fieldsSelectedByDefault: (fieldType: 'date' | 'iteration') =>
      `We've selected an existing ${fieldType} field in your project to get started. You can change the fields used here.`,
  },
  toolbarRoadmapOptions: 'Roadmap options',
  toolbarDateFields: 'Date fields',
  toolbarDateFieldsAriaLabel: 'Select date fields',
  toolbarToday: 'Today',
  toolbarZoomAriaLabel: (zoomLevel: RoadmapZoomLevel) => `Zoom level: ${zoomLevel}.`,
  zoomMenuDialogAriaLabel: (zoomLevel: RoadmapZoomLevel) =>
    `Select zoom level. The current zoom level is ${zoomLevel}.`,
  zoomLevel: 'Zoom level',
  zoomLevelOptions: {
    month: 'Month',
    quarter: 'Quarter',
    year: 'Year',
  },
  scrollToDateText: (date: Date) => `Scroll to: ${formatDateUtc(date)}`,
  scrollToPreviousPage: 'Scroll to previous date range',
  scrollToNextPage: 'Scroll to next date range',
  unableToSetDateFields: 'Unable to set date fields',
  unableToExpandDateRange: 'Unable to expand date selection',
}

export const ProjectMigrationResources = {
  statuses: {
    pending: 'Starting migration…',
    in_progress_project_details: 'Setting up the project…',
    in_progress_status_fields: 'Creating project columns…',
    in_progress_default_view: 'Creating an initial view…',
    in_progress_permissions: 'Migrating permissions…',
    in_progress_items: 'Migrating issues, pull requests, and drafts…',
    in_progress_workflows: 'Migrating workflows…',
    error: 'Your project could not be migrated.',
  },
  yourClassicProject: 'Your classic project',
  completedAndClosed1: 'has been closed and is read-only.',
  completedAndClosed2: 'Users visiting the classic project will be linked to this one.',
  completedAndStillOpen: "is still open, but future changes won't be synchronized.",
  automatedMigrationCompleteMessage: 'This project has been migrated as part of the sunset for Projects (classic).',
  automatedMigrationInProgressMessage: 'This project is being migrated as part of the sunset for Projects (classic).',
  sunsetNoticeLink: 'https://github.blog/changelog/2024-05-23-sunset-notice-projects-classic/',
  learnMore: 'Learn more',
  migrationCompleteMessage: 'Migration complete',
  somethingWentWrongMessage: 'Something went wrong',
}

export const ParentIssueResources = {
  addedUnsupportedItemToast: 'PRs and Drafts can’t be added as sub-issues. Item was added to No Parent issue group',
  toasts: {
    draftAndPullsNotSupported: {
      type: ToastType.error,
      message: 'PRs and draft issues are not supported by sub-issues',
    },
  },
}

export const MigrationResources = {
  migratingTitle: (title: string) => `Migrating ${title}`,
}

export const BulkUpdateResources = {
  startMessage: 'Bulk update pending',
  progressMessage: (percentage: number) => `Bulk update in progress ${percentage}%`,
  successMessage: (skippedCount: number) => {
    const makePlural = skippedCount > 1
    return `Bulk update completed.${
      skippedCount > 0
        ? ` ${skippedCount} item${makePlural ? 's' : ''} couldn’t be updated and ${
            makePlural ? 'were' : 'was'
          } skipped.`
        : ''
    }`
  },
  refreshPageMessage: (amount: number) =>
    `Bulk update failed${amount > 0 ? ` for ${amount} items` : ''}. Please refresh the page and try again.`,
}

export const BulkCopyResources = {
  failureMessage: 'An error occurred while copying draft issues.',
  successMessage: 'Draft issues copied successfully.',
  copyingDraftsMessage: 'Copying draft issues to your new project. This may take a minute to complete.',
}

export const BulkAddResources = {
  startMessage: 'Importing items from your repository…',
  progressMessage: (percentage: number) => `Bulk add in progress ${percentage}%`,
  successMessage: (addedCount: number, failedCount: number, initialImport: boolean) =>
    `Successfully added ${addedCount} items${failedCount > 0 ? `. ${failedCount} items failed to import` : ''}${
      initialImport ? '. Now running setup workflows - this will take a couple minutes.' : '.'
    }`,
  failedMessage: (failedCount: number) =>
    `Failed to add items from your repository. ${failedCount} ${singularOrPlural(
      failedCount,
      'item',
      'items',
    )} failed to import.`,
}

export const ArchiveResources = {
  allSelectedMatchingItems: (totalCount: {value: number; isApproximate: boolean}) =>
    `All ${totalCount.value.toLocaleString()}${totalCount.isApproximate ? `+` : ''} items matching query selected`,
  selectAllMatchingItems: (totalCount: {value: number; isApproximate: boolean}) =>
    `Select all ${totalCount.value.toLocaleString()}${totalCount.isApproximate ? `+` : ''} items matching this query`,
  selectedItems: (count: number) => `${count.toLocaleString()} ${singularOrPlural(count, 'item', 'items')} selected`,
  itemCount: (start: number, end: number, totalCount: {value: number; isApproximate: boolean}) =>
    `${start.toLocaleString()}-${end.toLocaleString()} of ${totalCount.value.toLocaleString()}${
      totalCount.isApproximate ? `+` : ''
    } ${singularOrPlural(totalCount.value, 'item', 'items')}`,
  noArchivedItems: 'No archived items',
  deleteAllMatchingItemsConfirmation: 'Are you sure you want to delete all items matching this query?',
}

export const IssueViewerResources = {
  openInNewTab: 'Open in new tab',
  copyLinkInProject: 'Copy link in project',
  archiveInProject: 'Archive in project',
  deleteFromProject: 'Delete from project',
}

export const StatusUpdatesResources = {
  title: 'Status updates',
  nullStateTitle: 'Add a project status update',
  description:
    "Status updates are brief reports tracking your project's health and progress. Begin by adding an update.",
  learnMoreLinkText: 'Learn more',
  addUpdateButtonText: 'Add update',
  saveUpdateButtonText: 'Save update',
  cancelUpdateButtonText: 'Cancel',
  addUpdateTitle: 'Add status update',
  statusFieldLabel: 'Status',
  startDateFieldLabel: 'Start date',
  targetDateFieldLabel: 'Target date',
  statusPickerFilterPlaceholderText: 'Filter statuses',
  statusPickerResultsAriaLabel: 'Status results',
  statusPickerTitle: 'Select a status',
  statusPickerAriaLabel: 'Select status',
  startDatePickerAriaLabel: 'Select start date',
  targetDatePickerAriaLabel: 'Select target date',
  confirmDeleteTitle: 'Delete status update',
  confirmDeleteBody: "Are you sure you'd like to delete this status update?",
  subscribeToUpdates: 'Subscribe to status updates for this project',
  unsubscribeFromUpdates: 'Unsubscribe from status updates for this project',
}

export const CollaboratorsTableResources = {
  error: 'An error has occurred.',
  noCollaborators: "You don't have any collaborators yet.",
  filtering: 'No collaborators found.',
  collaboratorsFound: (count: number) => `${count} ${singularOrPlural(count, 'collaborator', 'collaborators')} found.`,
}

export const IssueTypeResources = {
  setup: 'Set up issue types in your project',
  renameCustomType: (name: string) =>
    `Rename your “${name}“ custom field and start using issue types defined by your organization.`,
  addedUnsupportedItemToast: 'Drafts can’t be added with an issue type. Item was added to No type group',
  learnMoreText: 'Learn more about issue types.',
  learnMoreLink: 'https://gh.io/issue-types-learn-more',
}

export const UserNoticeResources = {
  IssueTypeRenamePopover: {
    title: IssueTypeResources.setup,
    description: IssueTypeResources.renameCustomType,
    altText: 'Issues create dialog with issue type selection',
    actionPrimary: 'Get Started',
    actionSecondary: 'I’ll do it later',
  },
}

export const ColumnBannerResources = {
  IssueTypeRename: {
    copy1: 'Action required',
    copy2: IssueTypeResources.renameCustomType,
    learnMoreLink: IssueTypeResources.learnMoreLink,
    learnMoreText: IssueTypeResources.learnMoreText,
    actionPrimary: 'Get Started',
  },
}

export const ParentIssueReassignmentConfirmationResources = {
  title: 'Are you sure?',
  content:
    'Adding this issue to the project will change its parent, because the current view is filtered or grouped by a parent issue. Are you sure you want to change the parent of this issue?',
  confirmButtonContent: 'Change parent issue',
}

export const FeedbackDialogResources = {
  limitExceededError: (limit: number) => `Please keep your feedback within ${limit} characters or less.`,
  noRatingError: 'Please select a rating.',
  submissionError: 'An error occurred while submitting your feedback.',
}

export const DefaultRepositoryPickerResources = {
  filterPlaceholder: 'Filter repositories',
  anchorButtonText: 'Select a repository',
  noReposAvailable: 'No repositories available',
  noReposAvailableBody: 'No repositories are available for selection.',
  noReposMatchThisSearch: 'No repositories match this search',
  noReposMatchThisSearchBody: 'Try a different search term or browse your repositories.',
}

export const DefaultIssueCreateRepositorySectionResources = {
  sectionTitle: 'Default repository',
  sectionCaption: 'By default, new issues created from this project are added to this repository.',
}

export const TemplateDialogResources = {
  bulkAddTitle: 'Bulk import items',
  bulkAddSubtitle: 'All new and existing items from the selected source will be added to this project.',
  autoAddItemCount: (itemCount: number) =>
    itemCount > 500
      ? 'The most recent 500 items will be added to the project'
      : `${itemCount} ${singularOrPlural(itemCount, 'item', 'items')} will be added`,
  importItemsBasedOnSearchQuery: 'Import items based on search query',
  invalidNoQuery: 'Please provide a valid search query',
  openIssues: 'Open issues',
  openPullRequests: 'Open pull requests',
  openIssuesAndPullRequests: 'Open issues and pull requests',
  selectItems: 'Select a repository',
  serverErrorMessage: 'We were unable to bulk import items at this time. Please try again.',
}

export const BulkAddQueryValidation = {
  requiredAdvancedSearchArgs: (needsOneOfObject: Array<{term: string; description: string}>) =>
    `Please specify ${joinOxford(
      needsOneOfObject.map(({description}) => description),
      'or',
    )} in your query.`,
  enterpriseRequiresExplicitRepoScoping:
    'Enterprise environments require explicit repository scoping (repo:owner/name) for security. Organization and user qualifiers alone are not permitted.',
}

export const IssueFieldResources = {
  issueField: 'Issue field',
  QuickAdd: {
    heading: 'Introducing issue fields 🗂️',
    description: 'Add issue fields to keep information consistent across your organization.',
    closeButtonLabel: 'Close',
    closeButtonAriaLabel: 'Dismiss issue field quick add actions',
    seeMoreButtonLabel: 'See more fields',
    addFieldButtonLabel: 'Add',
    addedFieldInlineMessage: 'Added',
    addFieldButtonAriaLabel: (name: string) => `Add ${name} field to project`,
    addFieldSuccess: (name: string) => `${name} field added to project`,
    addFieldError: (name: string) => `Could not add ${name} field to project. Please try again.`,
  },
  fieldNameCollisionText: 'Naming conflict',
  fieldNameCollisionTooltip: 'This field conflicts with an organization issue field. Rename this field.',
}

export const RenameProjectFieldDialogResources = {
  IssueFieldVariant: {
    title: 'Rename duplicate field',
    description: (name: string) => `In order to add the "${name}" issue field, you need to rename your project field.`,
    inputLabel: (name: string) => `Rename project field "${name}"`,
    actionPrimary: 'Rename',
    actionSecondary: 'Cancel',
    defaultInputString: (name: string) => `Project ${name}`,
  },

  IssueTypeVariant: {
    title: IssueTypeResources.setup,
    description: (name: string) =>
      `To start using issue types in your project, please rename your “${name}“ custom field and update any filters that reference it.`,
    learnMoreLink: IssueTypeResources.learnMoreLink,
    learnMoreText: IssueTypeResources.learnMoreText,
    fieldLabel: 'Rename field',
    actionPrimary: 'Finish setup',
    actionSecondary: 'Cancel',
    defaultInputString: 'Custom Type',
    videoDescription: 'Selecting the type field on a project, and applying Epic, Feature, and Task to project items',
  },
}

export const AddIssueFieldsDialogBannerResources = {
  fieldLimitReachedTitle: 'Field limit reached',
  noFieldsSelectedTitle: 'No fields selected',
  tooManyFieldsSelectedTitle: 'Too many fields selected',
  wouldExceedFieldLimitTitle: 'Unable to add selected fields',
  fieldLimitReachedDescription: (projectCount: number) =>
    `You already have ${projectCount} fields in your project. Remove some in order to add more fields.`,
  noFieldsSelectedDescription: 'Select at least one field to continue.',
  tooManyFieldsSelectedDescription: (maxFields: number) =>
    `You can only add ${maxFields} at a time. Deselect some fields to continue.`,
  wouldExceedFieldLimitDescription: (maxFields: number, projectCount: number) =>
    `You already have ${projectCount} fields in your project. Adding these would exceed the ${maxFields}-field limit.`,
  errorAddingFields: 'Something went wrong adding fields.',
  manageFieldsAction: 'Manage fields',
  tryAgainAction: 'Try again',
  serverErrorPatterns: {
    fieldLimitReached: 'Projects cannot have more than 50 fields',
  },
}
